/**
 * Centralized Session Management Service
 * Manages session tokens across all API services
 */

import { withIdentityHeaders } from './identityHeaders';

// Shared session token storage
let sessionToken = null;
let sessionInitialized = false;
let sessionInitPromise = null;

/**
 * Initialize session by making a lightweight API call
 * This will create a session on the backend and return the token
 * Note: This is a best-effort initialization. If it fails, sessions will
 * be created automatically on the first API call.
 */
const initializeSession = async () => {
    if (sessionInitialized && sessionToken) {
        return sessionToken;
    }

    // If initialization is already in progress, return the promise
    if (sessionInitPromise) {
        return sessionInitPromise;
    }

    // Start initialization
    sessionInitPromise = (async () => {
        try {
            // Try to load from localStorage first (persisted session)
            const storedToken = localStorage.getItem('sessionToken');
            if (storedToken) {
                sessionToken = storedToken;
                sessionInitialized = true;
                console.log('Session loaded from storage:', sessionToken);
                return sessionToken;
            }

            // Make a lightweight request to initialize session
            // The backend creates the session before reCAPTCHA validation,
            // so we'll get the token even if validation fails
            try {
                let baseUrl = process.env.REACT_APP_STUDSTOCK_ENDPOINT; 
                let url = `${baseUrl}/quotes/session-create`;
                const response = await fetch(url, {
                    method: 'GET',
                    headers: withIdentityHeaders({
                        'Content-Type': 'application/json'
                    })
                });
                
                // Store session token from response
                const newSessionToken = response.headers.get('X-Session-Token');
                if (newSessionToken) {
                    SessionService.setSessionToken(newSessionToken);
                }
                
            } catch (fetchError) {
                // Even if the request fails, we might have gotten headers
                console.warn('Session initialization request failed, will retry on first API call:', fetchError);
            }

            // If we couldn't get a token, mark as initialized anyway
            // The session will be created on the first actual API call
            sessionInitialized = true;
            return null;
        } catch (error) {
            console.error('Failed to initialize session:', error);
            // Mark as initialized even on error to avoid infinite retries
            sessionInitialized = true;
            return null;
        } finally {
            // Clear the promise so we can retry if needed
            sessionInitPromise = null;
        }
    })();

    return sessionInitPromise;
};

/**
 * Get the current session token
 * Will initialize session if not already done
 */
export const getSessionToken = async () => {
    if (sessionToken) {
        return sessionToken;
    }

    if (!sessionInitialized) {
        await initializeSession();
    }

    return sessionToken;
};

/**
 * Set session token (typically from API response)
 */
export const setSessionToken = (token) => {
    if (token) {
        sessionToken = token;
        sessionInitialized = true;
        // Persist to localStorage for session continuity
        try {
            localStorage.setItem('sessionToken', token);
        } catch (error) {
            console.warn('Failed to store session token in localStorage:', error);
        }
    }
};

/**
 * Get session token synchronously (returns null if not initialized)
 */
export const getSessionTokenSync = () => {
    return sessionToken;
};

/**
 * Check if session is initialized
 */
export const isSessionInitialized = () => {
    return sessionInitialized;
};

/**
 * Reset session (for testing or logout scenarios)
 */
export const resetSession = () => {
    sessionToken = null;
    sessionInitialized = false;
    sessionInitPromise = null;
    try {
        localStorage.removeItem('sessionToken');
    } catch (error) {
        console.warn('Failed to remove session token from localStorage:', error);
    }
};

/**
 * Validate session token with the backend
 * @param {string} token - Optional token to validate. If not provided, uses current session token
 * @returns {Promise<{valid: boolean, tokenType?: string, error?: string}>}
 */
export const validateSession = async (token = null) => {
    const tokenToValidate = token || sessionToken || getSessionTokenSync();
    
    if (!tokenToValidate) {
        return {
            valid: false,
            error: 'No session token available'
        };
    }

    try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
        if (!apiBaseUrl) {
            console.error('REACT_APP_API_BASE_URL not configured');
            return {
                valid: false,
                error: 'API base URL not configured'
            };
        }

        const response = await fetch(`${apiBaseUrl}/session/validate`, {
            method: 'GET',
            headers: withIdentityHeaders({
                'Content-Type': 'application/json',
                'X-Session-Token': tokenToValidate
            })
        });

        const data = await response.json();

        if (response.ok && data.valid) {
            // Update session token if returned in response
            if (response.headers.get('X-Session-Token')) {
                setSessionToken(response.headers.get('X-Session-Token'));
            }
            
            return {
                valid: true,
                tokenType: data.tokenType || 'UUID',
                sessionId: data.sessionId,
                decoded: data.decoded
            };
        } else {
            // Session is invalid - clear it
            if (tokenToValidate === sessionToken) {
                resetSession();
            }
            
            return {
                valid: false,
                error: data.error || 'Session validation failed',
                status: response.status
            };
        }
    } catch (error) {
        console.error('Session validation error:', error);
        return {
            valid: false,
            error: error.message || 'Failed to validate session'
        };
    }
};

const SessionService = {
    getSessionToken,
    setSessionToken,
    getSessionTokenSync,
    isSessionInitialized,
    initializeSession,
    resetSession,
    validateSession
};

export default SessionService;
